/* eslint-disable max-lines */
import { FilterList } from '@mui/icons-material'
import { useMediaQuery } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { GenericMenu, MenuItemProps, StyledMenu } from 'src/components/common/menu/GenericMenu'
import { ProjectFilter, ProjectFilterOption } from 'src/generated-types/project-filter'
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateRange } from '@mui/x-date-pickers-pro/internal/models'
import { format } from 'date-fns'

const ProjectFilterComponent: React.FC<{
  filterOptions: ProjectFilter
  setFilterOptions: React.Dispatch<React.SetStateAction<ProjectFilter>>
  menuItems: ProjectFilter
  setMenuItems: React.Dispatch<React.SetStateAction<ProjectFilter>>
}> = ({ filterOptions, setFilterOptions, menuItems }) => {
  const isTabletScreen = useMediaQuery('(max-width:1630px)')
  const { t } = useTranslation('projects')
  const [mainAnchorEl, setMainAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedItem, setSelectedItem] = useState<string[]>([])
  const [responsibleUserAnchorEl, setResponsibleUserAnchorEl] = useState<null | HTMLElement>(null)
  const [localityAnchorEl, setLocalityAnchorEl] = useState<null | HTMLElement>(null)
  const [ourReferenceAnchorEl, setOurReferenceAnchorEl] = useState<null | HTMLElement>(null)
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null)
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<null | HTMLElement>(null)
  const [dateRange, setDateRange] = React.useState<DateRange<Date>>([
    new Date(filterOptions.createdDate.startDate) ?? null,
    new Date(filterOptions.createdDate.endDate) ?? null,
  ])

  useEffect(() => {
    if (filterOptions.createdDate.startDate === '') setDateRange(prev => [null, prev[1]])
    if (filterOptions.createdDate.endDate === '') setDateRange(prev => [prev[0], null])
  }, [filterOptions.createdDate])

  const isMainMenuItemChecked = useMemo(
    () => ({
      localities: filterOptions?.localityIds && filterOptions.localityIds.length > 0,
      responsibleUsers: filterOptions?.responsibleUserIds && filterOptions.responsibleUserIds.length > 0,
      createdDate:
        filterOptions?.createdDate &&
        (filterOptions.createdDate.startDate !== '' || filterOptions.createdDate.endDate !== ''),
      status: filterOptions?.status && filterOptions.status.length > 0,
      ourReferences: filterOptions?.ourReferences && filterOptions.ourReferences.length > 0,
    }),
    [filterOptions],
  )

  const handleOpenMainMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMainAnchorEl(event.currentTarget)
  }

  const handleCloseAllMenus = () => {
    setMainAnchorEl(null)
    setResponsibleUserAnchorEl(null)
    setLocalityAnchorEl(null)
    setDateRangeAnchorEl(null)
    setOurReferenceAnchorEl(null)
    setStatusAnchorEl(null)
  }

  const menues = {
    responsibleUsers: t('responsibleUser', { ns: 'common' }),
    localities: t('locality', { ns: 'locality' }),
    status: t('status', { ns: 'projects' }),
    ourReferences: t('our-references', { ns: 'projects' }),
    createdDate: t('created-date', { ns: 'projects' }),
  }

  const handleItemClick = (name: string) => (event: React.MouseEvent<HTMLElement>) => {
    switch (name) {
      case 'responsibleUsers':
        event.stopPropagation()
        setSelectedItem([name])
        setResponsibleUserAnchorEl(event.currentTarget)
        break
      case 'localities':
        event.stopPropagation()
        setSelectedItem([name])
        setLocalityAnchorEl(event.currentTarget)
        break
      case 'createdDate':
        event.stopPropagation()
        setSelectedItem([name])
        setDateRangeAnchorEl(event.currentTarget)
        break
      case 'status':
        event.stopPropagation()
        setSelectedItem([name])
        setStatusAnchorEl(event.currentTarget)
        break
      case 'ourReferences':
        event.stopPropagation()
        setSelectedItem([name])
        setOurReferenceAnchorEl(event.currentTarget)
        break
    }
  }

  const mainMenuItems: MenuItemProps[] = Object.keys(menues).map(name => ({
    id: name,
    label: menues[name],
    checked: isMainMenuItemChecked[name],
    hasSubmenu: true,
    onClick: handleItemClick(name),
    selected: selectedItem?.includes(name),
    width: '238px',
    height: '48px',
  }))

  const handleFilterSelect = (item: ProjectFilterOption, filterKey: keyof ProjectFilter): void => {
    setFilterOptions(prev => {
      const currentItems = (prev[filterKey] as ProjectFilterOption[] | undefined) || []
      const isSelected = Array.isArray(currentItems) && currentItems.some(existingItem => existingItem.id === item.id)

      const updatedItems = isSelected
        ? currentItems.filter(existingItem => existingItem.id !== item.id)
        : [...currentItems, item]

      return {
        ...prev,
        [filterKey]: updatedItems.length > 0 ? updatedItems : [],
      }
    })
  }

  const handleLocalitySelect = (locality: ProjectFilterOption) => {
    handleFilterSelect(locality, 'localityIds')
  }

  const handleResponsibleUserSelect = (user: ProjectFilterOption) => {
    handleFilterSelect(user, 'responsibleUserIds')
  }

  const handleStatusSelect = (status: ProjectFilterOption) => {
    handleFilterSelect(status, 'status')
  }

  const handleOurReferenceSelect = (ref: ProjectFilterOption) => {
    handleFilterSelect(ref, 'ourReferences')
  }

  const generateMenuItems = (
    items: ProjectFilterOption[] | undefined,
    filterKey: keyof ProjectFilter,
    category: string,
    handleSelect: (item: ProjectFilterOption) => void,
    shouldTranslate = false,
  ): MenuItemProps[] => {
    return (items || [])
      .filter(item => item.id !== undefined && item.name !== undefined)
      .map(item => ({
        id: item.id as string | number,
        label: shouldTranslate ? t(item.name) : (item.name as string),
        checked:
          Array.isArray(filterOptions?.[filterKey]) &&
          (filterOptions[filterKey] as ProjectFilterOption[])?.some(filterItem => filterItem.id === item.id),
        onClick: () => {
          const menuItem = {
            id: item.id as string | number,
            name: item.name as string,
            category,
          }
          handleSelect(menuItem)
        },
        height: '36px',
      }))
  }

  const localityMenuItems: MenuItemProps[] = generateMenuItems(
    menuItems?.localityIds,
    'localityIds',
    t('locality', { ns: 'common' }),
    handleLocalitySelect,
  )

  const responsibleUserMenuItems: MenuItemProps[] = generateMenuItems(
    menuItems?.responsibleUserIds,
    'responsibleUserIds',
    t('responsibleUser', { ns: 'common' }),
    handleResponsibleUserSelect,
  )

  const statusMenuItems: MenuItemProps[] = generateMenuItems(
    menuItems?.status,
    'status',
    t('status', { ns: 'common' }),
    handleStatusSelect,
    true,
  )

  const ourReferenceMenuItems: MenuItemProps[] = generateMenuItems(
    menuItems?.ourReferences,
    'ourReferences',
    t('ourReferences', { ns: 'common' }),
    handleOurReferenceSelect,
  )

  return (
    <>
      <GridActionButton
        variant="outlined"
        height="44px"
        width="auto"
        onClick={handleOpenMainMenu}
        buttonText={isTabletScreen ? '' : t('filter', { ns: 'maintenance' })}
        buttonIcon={<FilterList />}
        buttonTextProps={{ fontWeight: 600, textTransform: 'uppercase' }}
      />
      {/* Main Menu */}
      <GenericMenu
        id="project-filter-menu"
        anchorEl={mainAnchorEl}
        isOpen={Boolean(mainAnchorEl)}
        onClose={handleCloseAllMenus}
        menuItems={mainMenuItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
      />

      {/* Responsible (customer contact) User Filter */}
      <GenericMenu
        id="responsible-user-filter-menu"
        anchorEl={responsibleUserAnchorEl}
        isOpen={Boolean(responsibleUserAnchorEl)}
        onClose={handleCloseAllMenus}
        menuItems={responsibleUserMenuItems}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
        MenuListProps={{
          onMouseLeave: e => {
            e.stopPropagation()
            setResponsibleUserAnchorEl(null)
          },
        }}
      />

      {/* Locality Filter */}
      <GenericMenu
        id="locality-filter-menu"
        anchorEl={localityAnchorEl}
        isOpen={Boolean(localityAnchorEl)}
        onClose={handleCloseAllMenus}
        menuItems={localityMenuItems}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
        MenuListProps={{
          onMouseLeave: e => {
            e.stopPropagation()
            setLocalityAnchorEl(null)
          },
        }}
      />

      {/* Status Filter */}
      <GenericMenu
        id="locality-filter-menu"
        anchorEl={statusAnchorEl}
        isOpen={Boolean(statusAnchorEl)}
        onClose={handleCloseAllMenus}
        menuItems={statusMenuItems}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
        MenuListProps={{
          onMouseLeave: e => {
            e.stopPropagation()
            setStatusAnchorEl(null)
          },
        }}
      />

      {/* Our reference Filter */}
      <GenericMenu
        id="locality-filter-menu"
        anchorEl={ourReferenceAnchorEl}
        isOpen={Boolean(ourReferenceAnchorEl)}
        onClose={handleCloseAllMenus}
        menuItems={ourReferenceMenuItems}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
        MenuListProps={{
          onMouseLeave: e => {
            e.stopPropagation()
            setOurReferenceAnchorEl(null)
          },
        }}
      />

      {/* created date Filter */}
      <StyledMenu
        id="created-date-filter-menu"
        anchorEl={dateRangeAnchorEl}
        open={Boolean(dateRangeAnchorEl)}
        onClose={handleCloseAllMenus}
        maxHeight={500}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transitionDuration={0}
        MenuListProps={{
          onMouseLeave: e => {
            e.stopPropagation()
            setDateRangeAnchorEl(null)
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDateRangePicker
            displayStaticWrapperAs="desktop"
            value={dateRange}
            onChange={newValue => {
              setDateRange(newValue)
              setFilterOptions(prev => ({
                ...prev,
                createdDate: {
                  // eslint-disable-next-line quotes
                  startDate: newValue[0] ? format(newValue[0], "yyyy-MM-dd'T'00:00:00+00:00") : '',
                  // eslint-disable-next-line quotes
                  endDate: newValue[1] ? format(newValue[1], "yyyy-MM-dd'T'00:00:00+00:00") : '',
                },
              }))
            }}
            renderInput={() => <></>}
          />
        </LocalizationProvider>
      </StyledMenu>
    </>
  )
}

export default ProjectFilterComponent
